import { useState } from 'react'
import type { AiAgentShell } from '../../api/settingsAiAgent'
import { num, shortNum } from './parts'

/**
 * Cheltuiala agentului pe 30 de zile: cifrele si graficul pe zile.
 *
 * Graficul e SVG scris aici, din datele serverului - nu o biblioteca de o suta
 * de kilobyti pentru o singura curba, exact ca in macheta veche. Doua arii
 * suprapuse: tokenii de intrare jos, cei de iesire deasupra; cursorul pe zi
 * arata cifrele zilei. Zilele fara nimic sunt zero, nu gauri.
 */

const W = 360
const H = 150
const PL = 36
const PR = 8
const PT = 10
const PB = 22

export function UsagePanel({ page }: { page: AiAgentShell }) {
  const [hover, setHover] = useState<number | null>(null)
  const daily = page.usageDaily ?? []
  const usage = page.usage
  const n = daily.length

  const cachedTotal = daily.reduce((sum, d) => sum + Number(d.cache_read || 0), 0)
  const cachedShare =
    cachedTotal + Number(usage.tokens_in) > 0
      ? Math.round((cachedTotal * 100) / (cachedTotal + Number(usage.tokens_in)))
      : null

  let peak = 1
  for (const d of daily) peak = Math.max(peak, Number(d.tokens_in) + Number(d.tokens_out))

  /* Plafonul "frumos": 1, 2, 5 x 10^k deasupra varfului. */
  const mag = Math.pow(10, Math.floor(Math.log10(peak)))
  let top = mag
  for (const m of [1, 2, 5, 10]) {
    if (m * mag >= peak) {
      top = m * mag
      break
    }
  }

  const x = (i: number) => (n > 1 ? PL + (i * (W - PL - PR)) / (n - 1) : PL)
  const y = (v: number) => PT + (H - PT - PB) * (1 - Math.min(1, v / top))
  const base = H - PB

  let lineIn = ''
  let lineTot = ''
  let backIn = ''
  daily.forEach((d, i) => {
    const vi = Number(d.tokens_in)
    const vt = vi + Number(d.tokens_out)
    const px = round(x(i))
    lineIn += (i ? ' L' : 'M') + px + ',' + round(y(vi))
    lineTot += (i ? ' L' : 'M') + px + ',' + round(y(vt))
    backIn = ' L' + px + ',' + round(y(vi)) + backIn
  })

  const areaIn = n > 0 ? `${lineIn} L${round(x(n - 1))},${base} L${round(x(0))},${base} Z` : ''
  /* Aria de iesire: intre linia totalului si linia intrarii. */
  const areaOut = n > 0 ? `${lineTot}${backIn} Z` : ''
  const hasAny = peak > 1
  const shown = hover !== null ? daily[hover] : null

  return (
    <aside className="aia-setup-side">
      <div className="panel panel-default aia-usage">
        <div className="panel-body">
          <div className="aia-usage-head">
            <strong>Usage</strong>
            <span className="text-muted">
              last 30 days, {page.scope === 'company' ? page.companyName : 'all companies'}
            </span>
          </div>

          <div className="aia-kpis">
            <div className="aia-kpi">
              <span className="aia-kpi-v">{num(usage.messages)}</span>
              <span className="aia-kpi-k">answers</span>
            </div>
            <div className="aia-kpi">
              <span className="aia-kpi-v">{shortNum(usage.tokens_in)}</span>
              <span className="aia-kpi-k">tokens in</span>
            </div>
            <div className="aia-kpi">
              <span className="aia-kpi-v">{shortNum(usage.tokens_out)}</span>
              <span className="aia-kpi-k">tokens out</span>
            </div>
            <div className="aia-kpi">
              <span className="aia-kpi-v">{cachedShare === null ? '–' : cachedShare + '%'}</span>
              <span className="aia-kpi-k">from cache</span>
            </div>
          </div>

          <div className="aia-chart-wrap">
            <svg
              className="aia-chart"
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label="Tokens per day, last 30 days"
              onMouseMove={(event) => {
                if (n < 1) return
                const rect = event.currentTarget.getBoundingClientRect()
                const vx = ((event.clientX - rect.left) * W) / rect.width
                const step = (W - PL - PR) / Math.max(1, n - 1)
                setHover(Math.max(0, Math.min(n - 1, Math.round((vx - PL) / step))))
              }}
              onMouseLeave={() => setHover(null)}
            >
              <defs>
                <linearGradient id="aiaGradIn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#0b6e99" stopOpacity=".55" />
                  <stop offset="1" stopColor="#0b6e99" stopOpacity=".08" />
                </linearGradient>
                <linearGradient id="aiaGradOut" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#4fb3e0" stopOpacity=".55" />
                  <stop offset="1" stopColor="#4fb3e0" stopOpacity=".10" />
                </linearGradient>
              </defs>
              {[0, 1, 2, 3, 4].map((g) => {
                const gy = round(y((top * g) / 4))
                return (
                  <g key={g}>
                    <line x1={PL} y1={gy} x2={W - PR} y2={gy} className="aia-grid" />
                    <text x={PL - 4} y={gy + 3} className="aia-axis" textAnchor="end">
                      {shortNum((top * g) / 4)}
                    </text>
                  </g>
                )
              })}
              {n > 0 ? (
                <>
                  <path d={areaOut} fill="url(#aiaGradOut)" />
                  <path d={areaIn} fill="url(#aiaGradIn)" />
                  <path d={lineTot} className="aia-line aia-line-out" />
                  <path d={lineIn} className="aia-line aia-line-in" />
                  {[0, Math.floor((n - 1) / 2), n - 1].map((li, k) =>
                    daily[li] ? (
                      <text
                        key={k}
                        x={round(x(li))}
                        y={H - 6}
                        className="aia-axis"
                        textAnchor={li === 0 ? 'start' : li === n - 1 ? 'end' : 'middle'}
                      >
                        {dayLabel(daily[li].day)}
                      </text>
                    ) : null
                  )}
                  {hover !== null && daily[hover] ? (
                    <>
                      <line className="aia-cursor" x1={round(x(hover))} y1={PT} x2={round(x(hover))} y2={base} />
                      <circle
                        className="aia-dot aia-dot-in"
                        r={3}
                        cx={round(x(hover))}
                        cy={round(y(Number(daily[hover].tokens_in)))}
                      />
                      <circle
                        className="aia-dot aia-dot-out"
                        r={3}
                        cx={round(x(hover))}
                        cy={round(y(Number(daily[hover].tokens_in) + Number(daily[hover].tokens_out)))}
                      />
                    </>
                  ) : null}
                </>
              ) : null}
            </svg>
            {!hasAny ? (
              <div className="aia-chart-empty">
                No usage in the last 30 days
                {daily.length === 0 ? ' (or the conversation tables are not there yet)' : ''}.
              </div>
            ) : null}
            {shown ? (
              <div className="aia-tip" style={{ left: 8 }}>
                <strong>{shown.day}</strong>
                <br />
                {num(shown.messages)} answers
                <br />
                {num(shown.tokens_in)} in · {num(shown.tokens_out)} out
                {shown.cache_read > 0 ? (
                  <>
                    <br />
                    {num(shown.cache_read)} from cache
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="aia-legend">
            <span>
              <i className="aia-sw aia-sw-in"></i> tokens in
            </span>
            <span>
              <i className="aia-sw aia-sw-out"></i> tokens out
            </span>
          </div>
        </div>
      </div>
    </aside>
  )
}

function round(v: number): number {
  return Math.round(v * 10) / 10
}

/** `date('j M')` din macheta: "8 Sep". */
function dayLabel(day: string): string {
  const parsed = new Date(day + 'T00:00:00')

  return Number.isNaN(parsed.getTime())
    ? day
    : `${parsed.getDate()} ${parsed.toLocaleString('en-US', { month: 'short' })}`
}
